import React from 'react';
import _ from 'lodash';
import moment from 'moment';
import classnames from 'classnames';
import history from 'app/routes/history';
import {
  getPaymentEventsForDashboard,
  getPaymentEventForDashboard,
} from 'app/ducks/paymentEvents/selectors';

import { combinedTime, convertToUtc, superSimpleDate } from 'app/utils/parsers';
import { checkSetState } from '@teammo/teammo-general-utils';

import EventSidebar from './EventSidebar';
import EventsRow from './EventsRow';
import EventDashboardControl from './EventDashboardControl';
import EventReport from './EventReport';
import MatchList from './MatchList';
import EventDashboardControlTablet from './EventDashboardControlTablet';

interface IEventDashboardSimpleProps {
  match: any;
  actions: any;
  organisations: any;
  paymentEvents: any;
  paymentEventDashboard: any;
  paymentEventDashboardConfig: any;
  matchPayments: any;
  filterConfig: any;
  appReady: boolean;
  publicDashboard: boolean;
  importButton: boolean;
  eventOnlyView: boolean;
  eventDashboardId: string;
}

interface IEventDashboardSimpleState {
  time: string;
  parsedTime: string;
  filterConfig: any;
  appliedFilterConfig: any;
  selectedEvent: any;
  eventSidebarOpen: boolean;
  changed: boolean;
}
export default class EventDashboardSimple extends React.Component<
  IEventDashboardSimpleProps,
  IEventDashboardSimpleState
> {
  intervalId;

  static defaultProps: IEventDashboardSimpleProps = {
    match: undefined,
    actions: undefined,
    organisations: undefined,
    paymentEvents: undefined,
    paymentEventDashboard: undefined,
    paymentEventDashboardConfig: {},
    matchPayments: undefined,
    appReady: false,
    publicDashboard: false,
    importButton: false,
    eventOnlyView: false,
    eventDashboardId: undefined,
    filterConfig: undefined,
  };

  state: IEventDashboardSimpleState = {
    time: this.props.paymentEventDashboardConfig.time,
    parsedTime: this.props.paymentEventDashboardConfig.parsedTime,
    filterConfig: this.props.filterConfig || {},
    appliedFilterConfig: undefined,
    selectedEvent: undefined,
    eventSidebarOpen: false,
    changed: false,
  };

  componentDidMount(): void {
    this.loadPaymentEvents();
    // this.props.actions.getOrganisation()
    this.intervalID = setInterval(this.loadPaymentEvents.bind(this), 20000);


    checkSetState(this.state, this.props.paymentEventDashboard);
  }

  componentWillUnmount(): void {
    clearInterval(this.intervalID);
  }

  static getDerivedStateFromProps(newProps, prevState): any {
    return checkSetState(prevState, newProps.paymentEventDashboardConfig);
  }

  componentDidUpdate = (): void => {
    if (this.state.changed) {
      this.loadPaymentEvents();
    }
  }

  getTimeRange = (): any => {
    const date = this.state.time === undefined ? new Date() : new Date(this.state.time);
    const sinceDate = moment(date).startOf('day');
    const untilDate = moment(date).endOf('day');
    const sinceTime = convertToUtc(sinceDate.format());
    const untilTime = convertToUtc(untilDate.format());

    return { sinceTime, untilTime };
  };

  getLocationInfoFilter = (): any => {
    if (
      this.state.filterConfig !== undefined
      && this.state.filterConfig.venue !== undefined
    ) {
      const location = _.isArray(this.state.filterConfig.venue) ? _.join(_.map(this.state.filterConfig.venue, (venue) => venue.id), ',') : this.state.filterConfig.venue.id;

      return { 'location-name': location };
    }
    return {};
  };

  loadPaymentEvents = (): any => {
    const { sinceTime, untilTime } = this.getTimeRange();
    const locationInfoFilter = this.getLocationInfoFilter();
    if (this.props.eventDashboardId === undefined) {
      this.props.actions.getAllPaymentEvents(
        {
          filter: {
            'match-at.since': sinceTime,
            'match-at.until': untilTime,
            ...locationInfoFilter,
          },
          page: {
            size: 5000,
            number: 0,
          },
        },
        false,
      );
    } else {
      this.props.actions.getAllPaymentEvents(
        {
          filter: {
            'match-at.since': sinceTime,
            'match-at.until': untilTime,
            ...locationInfoFilter,
          },
          sort: 'match-at,location-name,section-name',
          page: {
            size: 5000,
            number: 0,
          },
        },
        false,
        this.props.eventDashboardId,
      );
    }
    this.setState({ changed: false });
  };

  handleChangeTime = (e): void => {
    const selectedDateTime = e[0].toString();
    const currentTime = new Date();
    const combinedDateTime = combinedTime(
      selectedDateTime,
      currentTime.toString(),
    );

    this.setState({
      time: selectedDateTime,
      parsedTime: combinedDateTime,
    });

    this.props.actions.updatePaymentEventDashboardConfig({
      time: selectedDateTime,
      parsedTime: combinedDateTime,
    });
    this.loadPaymentEvents();
  };

  handleEventClick = (event): void => {
    if (event === this.state.selectedEvent) {
      this.setState({
        selectedEvent: undefined,
        eventSidebarOpen: false,
      });
    } else {
      this.setState({
        selectedEvent: event,
        eventSidebarOpen: true,
      });
    }
  };

  handleSidebarClose = (): void => {
    this.setState({ eventSidebarOpen: false });
  };

  handleFilterChange = (e, name = null): void => {
    let config;
    // Remove filter value
    if (e === null || _.isEmpty(e)) {
      config = _.omit(
        {
          ...this.state.filterConfig,
        },
        name,
      );
      // Handle custom inputs
    } else if (e.target === undefined) {
      config = { ...this.state.filterConfig };
      if (_.isArray(e)) {
        const parsedConfig = _.map(e, (f) => {
          if (f.__isNew__) {
            return { id: f.value, name: f.value };
          }
          return f;
        });
        config[name] = parsedConfig;
      } else {
        config = {
          ...this.state.filterConfig,
          [name]: e,
        };
      }
      // Handle regular inputs
    } else if (_.isEmpty(e.target.value) || e.target.value === '') {
      config = _.omit(
        {
          ...this.state.filterConfig,
        },
        e.target.name,
      );
      // Handle regular inputs fallback
    } else {
      config = {
        ...this.state.filterConfig,
        [e.target.name]: e.target.value,
      };
    }
    this.props.actions.updatePaymentEventDashboardConfig({
      filterConfig: config,
    });
    this.setState({
      filterConfig: config,
      appliedFilterConfig: this.applyFilters(config),
      changed: true,
    });
  };

  applyFilters = (config): any => {
    const appliedFilterConfig = _.flatMap(config, (value, key) => {
      if (_.isArray(value)) {
        return _.map(value, (innerValue, innerKey) => {
          const innerParsedValue = _.isObject(innerValue)
            ? innerValue.name
            : innerValue;
          return { id: key, value: innerParsedValue };
        });
      }
      const parsedValue = _.isObject(value) ? value.id : value;
      return { id: key, value: parsedValue };
    });
    return appliedFilterConfig;
  };

  clearFilters = (): void => {
    this.setState({
      filterConfig: {},
      appliedFilterConfig: [],
    });
  };

  toggleReport = (): void => {
    if (this.props.match.path === '/event-dashboard/:eventDashboardId/report') {
      history.push(`/event-dashboard/${this.props.eventDashboardId}`);
    } else {
      history.push(`/event-dashboard/${this.props.eventDashboardId}/report`);
    }
  };

  toggleListView = (): void => {
    if (this.props.match.path === '/event-dashboard/:eventDashboardId/list' || this.props.match.path === '/event-dashboard/:eventDashboardId/list\\::filterConfig') {
      history.push(`/event-dashboard/${this.props.eventDashboardId}`);
    } else {
      history.push(`/event-dashboard/${this.props.eventDashboardId}/list`);
    }
  };

  renderPaymentRows = (paymentEvents): React.ReactNode => {
    let counter = 0;
    return _.map(paymentEvents, (paymentEvent, key) => {
      counter += 1;
      return (
        <EventsRow
          selectedId={this.state.selectedEvent}
          key={counter}
          time={key}
          events={paymentEvent}
          handleEventClick={this.handleEventClick}
        />
      );
    });
  };

  renderEvents = (paymentRows): React.ReactNode => {
    if (paymentRows !== undefined && _.keys(paymentRows).length > 0) {
      return (
        <div className={classnames('row h-100', { 'side-bar-open': this.state.eventSidebarOpen })}>
          <div className="col h-100">{this.renderPaymentRows(paymentRows)}</div>
        </div>
      );
    }
    const simpleDate = superSimpleDate(this.state.parsedTime);

    return (
      <div className="row h-100 with-margin-top-10">
        <div className="col h-100">
          <h2 className="text-center with-margin-top-30">
            No matches found for
            {' '}
            <span className="text-weight-bold">{simpleDate}</span>
            .
          </h2>
        </div>
      </div>
    );
  };

  renderContent = (paymentRows): React.ReactNode => {
    if (this.props.match.path === '/event-dashboard/:eventDashboardId/report') {
      return <EventReport {...this.props.paymentEvents} />;
    }
    if (this.props.match.path === '/event-dashboard/:eventDashboardId/list' || this.props.match.path === '/event-dashboard/:eventDashboardId/list\\::filterConfig') {
      const { sinceTime, untilTime } = this.getTimeRange();
      const locationInfoFilter = this.getLocationInfoFilter();

      return (
        <MatchList
          sinceTime={sinceTime}
          untilTime={untilTime}
          locationInfoFilter={locationInfoFilter}
          {...this.props.paymentEvents}
          match={this.props.match}
        />
      );
    }
    return this.renderEvents(paymentRows);
  };

  render(): React.ReactNode {
    const paymentRows = getPaymentEventsForDashboard(
      this.props.paymentEvents.paymentEventsById,
      this.props.paymentEvents.paymentEventIds,
    );
    const selectedEvent = this.state.selectedEvent === undefined
      ? {}
      : getPaymentEventForDashboard(
        this.props.paymentEvents.paymentEventsById[this.state.selectedEvent],
      );
    return (
      <div className="event-dashboard-app h-100-minus-footer">
        <EventSidebar
          {...selectedEvent}
          subscriptionPayments={this.props.subscriptionPayments}
          fixtureMatchDashboardId={this.props.eventDashboardId}
          isOpen={this.state.eventSidebarOpen}
          handleSidebarClose={this.handleSidebarClose}
          updatePaymentEvent={this.props.actions.updatePaymentEvent}
          updateSubscriptionPayment={this.props.actions.updateSubscriptionPayment}
          getPaymentEvent={this.props.actions.getPaymentEvent}
        />
        <div className="container-fluid m-h-100">
          <div className="h-100-minus-header">
            {this.props.publicDashboard && (
              <EventDashboardControlTablet
                paymentEventDashboard={this.props.paymentEventDashboard}
                toggleViewType={this.toggleListView}
                toggleReport={this.toggleReport}
                match={this.props.match}
                importButton={this.props.importButton}
                orgName={this.props.organisations.currentOrganisation.name}
                parsedTime={this.state.parsedTime}
                handleChangeTime={this.handleChangeTime}
                eventOnlyView={this.props.eventOnlyView}
                isLoading={this.props.paymentEvents.isLoading}
                loadPaymentEvents={this.loadPaymentEvents}
                paymentEvents={this.props.paymentEvents.paymentEventsById}
                handleFilterChange={this.handleFilterChange}
                applyFilters={this.applyFilters}
                clearFilters={this.clearFilters}
                filterConfig={this.state.filterConfig}
              />
            )}

            {/* {!this.props.publicDashboard && (
              <EventDashboardControl
                paymentEventDashboard={this.props.paymentEventDashboard}
                toggleReport={this.toggleReport}
                importButton={this.props.importButton}
                orgName={this.props.organisations.currentOrganisation.name}
                parsedTime={this.state.parsedTime}
                handleChangeTime={this.handleChangeTime}
                eventOnlyView={this.props.eventOnlyView}
                isLoading={this.props.paymentEvents.isLoading}
                loadPaymentEvents={this.loadPaymentEvents}
              />
            )} */}
            {this.renderContent(paymentRows)}
          </div>
        </div>
      </div>
    );
  }
}
